<script setup lang="ts">
/**
 * The catch-all for a URL none of this app's hand-rolled routes recognise;
 * see `state/not-found.ts` for what "recognise" means here.
 *
 * The way home is a plain `<a href="/">` rather than a call into the app's own
 * navigation: this screen renders instead of everything else in `App.vue`'s
 * `phase`, so there is no session state, scene or history to hand back into,
 * and a full navigation is the only honest way out. It wears the painted
 * plate by reusing the kit's yellow button image (`ActionButton` renders a
 * `<button>`, which cannot be a link).
 *
 * The same steel card as every pre-game screen (AuthShell): the compass lost
 * its bearing, `404` in game lettering, one short line, one yellow plate.
 */
import { useI18n } from '@/i18n';
import AuthShell from './AuthShell.vue';
import AuthStatus from './auth/AuthStatus.vue';

const { t } = useI18n();
</script>

<template>
  <AuthShell test-id="not-found" icon="compass" :title="t('notFound.title')">
    <AuthStatus icon="binoculars" :title="t('notFound.code')">{{ t('notFound.hint') }}</AuthStatus>
    <a class="back" href="/">{{ t('notFound.back') }}</a>
  </AuthShell>
</template>

<style scoped>
/* ActionButton's primary plate, drawn on a link: same 9-slice, same lettering (see panel/ActionButton.vue). */
.back {
  --h: 3.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: var(--h);
  padding: 0 calc(var(--h) * 0.55) calc(var(--h) * 0.06);
  border: 0 solid transparent;
  border-image: url('/art/ui/kit/btn_primary.webp') 50% 12% fill / calc(var(--h) / 2) calc(var(--h) * 0.42) stretch;
  color: #2a1d02;
  font-family: var(--font-game);
  font-size: calc(var(--h) * 0.38);
  letter-spacing: 0.03em;
  text-transform: uppercase;
  text-decoration: none;
  line-height: 1;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.45);
  touch-action: manipulation;
  filter: drop-shadow(0 3px 0 rgba(0, 0, 0, 0.35));
  transition: transform 110ms var(--ease), filter 110ms var(--ease);
}

.back:active {
  transform: translateY(3px);
}

@media (hover: hover) {
  .back:hover {
    filter: brightness(1.08) drop-shadow(0 4px 0 rgba(0, 0, 0, 0.35));
  }
}

.back:focus-visible {
  outline: 2px solid var(--kit-gold);
  outline-offset: 4px;
}
</style>
